import {
  AlertCircle,
  AlertTriangle,
  Building2,
  CheckCircle2,
  Link2,
  Loader2,
  Search,
  User,
} from "lucide-react";
import { type FormEvent, type ReactNode, useId, useMemo, useState } from "react";

import { BusinessHolderForm } from "@/modules/dashboard/cards/components/holder/BusinessHolderForm";
import { PersonHolderForm } from "@/modules/dashboard/cards/components/holder/PersonHolderForm";
import type { CreateIdentityRequest } from "@/modules/dashboard/cards/types/identity-form.types";
import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaBadge } from "@/shared/components/data-display/ColocaBadge";
import { ColocaAlert } from "@/shared/components/feedback/ColocaAlert";
import { ColocaAlertDialog } from "@/shared/components/feedback/ColocaAlertDialog";
import { ColocaSkeleton } from "@/shared/components/feedback/ColocaSkeleton";
import { ColocaInput } from "@/shared/components/forms/ColocaInput";
import { ColocaLabel } from "@/shared/components/forms/ColocaLabel";
import { ColocaTextarea } from "@/shared/components/forms/ColocaTextarea";
import { ColocaDrawer } from "@/shared/components/overlays/ColocaDrawer";
import { TwoFactorActionModals } from "@/shared/components/overlays/TwoFactorActionModals";
import type { VerificationTranslations } from "@/shared/lang/interfaces/verification";
import { useTranslations } from "@/shared/lang/useTranslations";
import { formatDateTime } from "@/shared/utils/dateTimeHelpers";
import { colocaToast } from "@/shared/utils/toast";

import { type KycApplicantLinkFailure, useKycApplicantLink } from "../../hooks/useKycApplicantLink";
import type { KycApplicantLinkIssue, KycApplicantLinkPreview } from "../../types/verification-api.types";
import { businessFormValuesFromDraft, personFormValuesFromDraft } from "../../utils/kycApplicantDraft";
import { KycStatusPill } from "../KycStatusPill";

const FORM_ID = "kyc-applicant-link-identity";
const REASON_MIN = 10;
const REASON_MAX = 500;

/** Codes with their own sentence; anything else falls back to the generic one with the code. */
const BLOCKER_CODES = new Set([
  "applicant_not_found",
  "applicant_not_approved",
  "applicant_review_changed",
  "applicant_linked_elsewhere",
  "applicant_owned_by_other_organization",
  "subject_kind_mismatch",
  "corporate_identity_already_verified",
  "identity_has_other_verification",
  "document_mismatch",
  "organization_kyc_provider_unsupported",
  "link_stale",
  "identity_required",
  "identity_not_allowed",
]);
const WARNING_CODES = new Set([
  "name_mismatch",
  "country_mismatch",
  "applicant_without_document",
  "applicant_created_for_other_identity",
]);

type Translate = ReturnType<typeof useTranslations<"verification">>["t"];

function blockerMessage(t: Translate, code: string, params?: Record<string, string>): string {
  return BLOCKER_CODES.has(code)
    ? t(`admin_link_blocker_${code}` as keyof VerificationTranslations, params)
    : t("admin_link_blocker_unknown", { code });
}

function warningMessage(t: Translate, issue: KycApplicantLinkIssue): string {
  return WARNING_CODES.has(issue.code)
    ? t(`admin_link_warning_${issue.code}` as keyof VerificationTranslations, issue.params)
    : t("admin_link_warning_unknown", { code: issue.code });
}

function failureMessage(t: Translate, failure: KycApplicantLinkFailure, genericText: string): string {
  if (failure.kind === "refused") return blockerMessage(t, failure.code);
  if (failure.kind === "provider_unavailable") return t("admin_link_provider_unavailable");
  return genericText;
}

interface LinkExistingVerificationDrawerProps {
  organizationId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Called after a successful link, so the host re-reads the organization's verification. */
  onLinked: () => void;
}

/**
 * Staff tool: link a KYC/KYB file approved outside the platform to the organization picked on
 * `/app/account-verification`. Search → what the file is and what linking will do → (when the organization
 * has no corporate identity) the identity pre-filled from the file → reason → confirm → TOTP.
 *
 * Never names the provider nor calls the id an applicant on screen (rule 12): it is the "KYC file".
 */
export function LinkExistingVerificationDrawer({
  organizationId,
  open,
  onOpenChange,
  onLinked,
}: LinkExistingVerificationDrawerProps) {
  const { t } = useTranslations("verification");
  const inputId = useId();
  const reasonId = useId();
  const reasonHintId = useId();
  const linking = useKycApplicantLink(organizationId);
  const [applicantId, setApplicantId] = useState("");
  const [reason, setReason] = useState("");
  const [reasonTouched, setReasonTouched] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  // The identity the form validated, held while the confirmation is open.
  const [pendingIdentity, setPendingIdentity] = useState<CreateIdentityRequest | undefined>(undefined);

  const { preview, previewing, previewFailure } = linking;
  const blocked = !!preview && (preview.blockers.length > 0 || !preview.applicant || !preview.action);
  const needsIdentity = !!preview?.draft && !preview.organization.corporate_identity;
  const reasonValid = reason.trim().length >= REASON_MIN;
  const organizationName = preview?.organization.name ?? "";
  const busy = linking.linking && !linking.twoFactor.challenge.open;

  const close = () => onOpenChange(false);

  const handleSearch = (event: FormEvent) => {
    event.preventDefault();
    const id = applicantId.trim();
    if (!id) return;
    setPendingIdentity(undefined);
    void linking.search(id);
  };

  const requestConfirm = (identity?: CreateIdentityRequest) => {
    setReasonTouched(true);
    if (!reasonValid) return;
    setPendingIdentity(identity);
    setConfirmOpen(true);
  };

  const handleConfirm = async () => {
    if (!preview?.applicant) return;
    setConfirmOpen(false);
    const result = await linking.link({
      applicant_id: preview.applicant.id,
      reason: reason.trim(),
      ...(pendingIdentity ? { identity: pendingIdentity } : {}),
    });
    if (!result) return;
    colocaToast.success(t("admin_link_done", { name: organizationName }));
    onLinked();
    close();
  };

  return (
    <>
      <ColocaDrawer
        open={open}
        onOpenChange={onOpenChange}
        size="xl"
        title={t("admin_link_title")}
        description={t("admin_link_description")}
        pinLastChild={false}
        footer={
          <div className="flex w-full flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <ColocaButton variant="default" className="w-full sm:w-auto" onClick={close} disabled={busy}>
              {t("admin_link_cancel")}
            </ColocaButton>
            <ColocaButton
              variant="primary"
              className="w-full sm:w-auto"
              icon={Link2}
              loading={busy}
              disabled={!preview || blocked || previewing || busy}
              {...(needsIdentity
                ? { type: "submit" as const, form: FORM_ID, onClick: () => setReasonTouched(true) }
                : { onClick: () => requestConfirm() })}
            >
              {t("admin_link_submit")}
            </ColocaButton>
          </div>
        }
      >
        <form onSubmit={handleSearch} className="space-y-2">
          <ColocaLabel htmlFor={inputId}>{t("admin_link_id_label")}</ColocaLabel>
          <div className="flex flex-col gap-2 sm:flex-row">
            <ColocaInput
              id={inputId}
              value={applicantId}
              onValueChange={setApplicantId}
              placeholder={t("admin_link_id_placeholder")}
              autoComplete="off"
              spellCheck={false}
              className="font-mono"
              disabled={busy}
            />
            <ColocaButton
              type="submit"
              variant="default"
              icon={Search}
              className="w-full sm:w-auto"
              loading={previewing}
              disabled={!applicantId.trim() || busy}
            >
              {t("admin_link_search")}
            </ColocaButton>
          </div>
        </form>

        {previewing && (
          <div className="space-y-3" aria-live="polite">
            <span className="sr-only">{t("admin_link_searching")}</span>
            <ColocaSkeleton className="h-28 w-full" />
            <ColocaSkeleton className="h-16 w-full" />
          </div>
        )}

        {!previewing && previewFailure && (
          <ColocaAlert
            variant="destructive"
            icon={AlertCircle}
            text={failureMessage(t, previewFailure, t("error_generic_description"))}
          />
        )}

        {!previewing && preview && (
          <PreviewBody
            preview={preview}
            t={t}
            onIdentityValid={requestConfirm}
            reasonField={
              blocked ? null : (
                <div className="space-y-2">
                  <ColocaLabel htmlFor={reasonId}>{t("admin_link_reason_label")}</ColocaLabel>
                  <ColocaTextarea
                    id={reasonId}
                    value={reason}
                    onValueChange={setReason}
                    onBlur={() => setReasonTouched(true)}
                    placeholder={t("admin_link_reason_placeholder")}
                    maxLength={REASON_MAX}
                    rows={3}
                    aria-describedby={reasonHintId}
                    aria-invalid={reasonTouched && !reasonValid}
                    disabled={busy}
                  />
                  <p
                    id={reasonHintId}
                    className={
                      reasonTouched && !reasonValid
                        ? "text-xs text-destructive"
                        : "text-xs text-muted-foreground"
                    }
                  >
                    {reasonTouched && !reasonValid
                      ? t("admin_link_reason_too_short")
                      : t("admin_link_reason_hint")}
                  </p>
                </div>
              )
            }
          />
        )}

        {busy && <ColocaAlert variant="secondary" icon={Loader2} text={t("admin_link_linking")} />}

        {linking.linkFailure && (
          <ColocaAlert
            variant="destructive"
            icon={AlertCircle}
            text={failureMessage(t, linking.linkFailure, t("error_generic_description"))}
          />
        )}
      </ColocaDrawer>

      <ColocaAlertDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title={t("admin_link_confirm_title")}
        description={t("admin_link_confirm_description", { name: organizationName })}
        confirmLabel={t("admin_link_confirm")}
        cancelLabel={t("admin_link_cancel")}
        onConfirm={() => void handleConfirm()}
      />

      <TwoFactorActionModals
        controller={linking.twoFactor}
        requiredDescription={t("admin_link_two_factor_required")}
        challengeDescription={t("admin_link_two_factor_description", { name: organizationName })}
      />
    </>
  );
}

interface PreviewBodyProps {
  preview: KycApplicantLinkPreview;
  t: Translate;
  onIdentityValid: (identity: CreateIdentityRequest) => void;
  reasonField: ReactNode;
}

function PreviewBody({ preview, t, onIdentityValid, reasonField }: PreviewBodyProps) {
  const { activeLanguage } = useTranslations("verification");
  const { applicant, organization, draft, action, blockers, warnings } = preview;
  const corporate = organization.corporate_identity;
  const isBusiness = applicant?.subject_kind === "business";

  // Read once per file: the forms take their values on mount only.
  const initialValues = useMemo(
    () =>
      draft
        ? draft.type === "business"
          ? { business: businessFormValuesFromDraft(draft) }
          : { person: personFormValuesFromDraft(draft) }
        : null,
    [draft],
  );

  return (
    <div className="space-y-5">
      {applicant && (
        <section className="space-y-3 rounded-lg border border-border bg-card p-4">
          <h3 className="text-sm font-semibold text-foreground">{t("admin_link_summary_title")}</h3>
          <dl className="grid gap-4 sm:grid-cols-2">
            <Fact label={t("admin_link_field_kind")}>
              <ColocaBadge icon={isBusiness ? Building2 : User}>
                {isBusiness ? t("admin_link_subject_business") : t("admin_link_subject_individual")}
              </ColocaBadge>
            </Fact>
            <Fact label={t("admin_link_field_status")}>
              <KycStatusPill status={applicant.kyc_status} />
            </Fact>
            <Fact label={t("admin_link_field_name")}>
              <span className="text-sm font-medium text-foreground">{applicant.display_name ?? "—"}</span>
            </Fact>
            <Fact label={t("admin_link_field_reviewed_at")}>
              <span className="text-sm text-foreground">
                {applicant.reviewed_at
                  ? formatDateTime(applicant.reviewed_at, { locale: activeLanguage, includeTime: true })
                  : "—"}
              </span>
            </Fact>
            <Fact label={t("admin_link_field_documents")} wide>
              <span className="font-mono text-sm text-foreground">
                {applicant.document_numbers.length > 0 ? applicant.document_numbers.join(" · ") : "—"}
              </span>
            </Fact>
          </dl>
        </section>
      )}

      {corporate && (
        <section className="space-y-3 rounded-lg border border-border p-4">
          <h3 className="text-sm font-semibold text-foreground">{t("admin_link_current_title")}</h3>
          <dl className="grid gap-4 sm:grid-cols-3">
            <Fact label={t("admin_link_field_name")}>
              <span className="text-sm font-medium text-foreground">{corporate.display_name ?? "—"}</span>
            </Fact>
            <Fact label={t("admin_link_current_document")}>
              <span className="font-mono text-sm text-foreground">
                {corporate.identification_type} {corporate.identification_number}
              </span>
            </Fact>
            <Fact label={t("admin_link_field_status")}>
              <KycStatusPill status={corporate.kyc_status} />
            </Fact>
          </dl>
        </section>
      )}

      {blockers.length > 0 ? (
        <ColocaAlert
          variant="destructive"
          icon={AlertCircle}
          heading={t("admin_link_blockers_title")}
          text={<IssueList items={blockers.map((issue) => blockerMessage(t, issue.code, issue.params))} />}
        />
      ) : (
        action && (
          <ColocaAlert
            variant="success"
            icon={CheckCircle2}
            heading={t("admin_link_action_title")}
            text={t(`admin_link_action_${action}` as keyof VerificationTranslations, {
              name: organization.name,
            })}
          />
        )
      )}

      {warnings.length > 0 && (
        <ColocaAlert
          variant="warning"
          icon={AlertTriangle}
          heading={t("admin_link_warnings_title")}
          text={<IssueList items={warnings.map((issue) => warningMessage(t, issue))} />}
        />
      )}

      {blockers.length === 0 && initialValues && applicant && (
        <section className="space-y-3">
          <div className="space-y-1">
            <h3 className="text-sm font-semibold text-foreground">{t("admin_link_form_title")}</h3>
            <p className="text-xs text-muted-foreground">{t("admin_link_form_hint")}</p>
          </div>
          {preview.missing_fields.length > 0 && (
            <ColocaAlert
              variant="secondary"
              icon={AlertTriangle}
              text={t("admin_link_missing", { count: preview.missing_fields.length })}
            />
          )}
          {initialValues.business ? (
            <BusinessHolderForm
              key={applicant.id}
              formId={FORM_ID}
              initialValues={initialValues.business}
              onValidSubmit={(payload) => onIdentityValid({ ...payload, is_organization_identity: true })}
            />
          ) : (
            <PersonHolderForm
              key={applicant.id}
              formId={FORM_ID}
              initialValues={initialValues.person}
              onValidSubmit={(payload) => onIdentityValid({ ...payload, is_organization_identity: true })}
            />
          )}
        </section>
      )}

      {reasonField}
    </div>
  );
}

function Fact({ label, wide, children }: { label: string; wide?: boolean; children: ReactNode }) {
  return (
    <div className={wide ? "sm:col-span-2" : undefined}>
      <dt className="text-xs uppercase tracking-wide text-muted-foreground">{label}</dt>
      <dd className="mt-1">{children}</dd>
    </div>
  );
}

function IssueList({ items }: { items: string[] }) {
  if (items.length === 1) return <>{items[0]}</>;
  return (
    <ul className="list-disc space-y-1 pl-4">
      {items.map((item) => (
        <li key={item}>{item}</li>
      ))}
    </ul>
  );
}
